/**
 * Invoice download filename helpers.
 *
 * Canonical pattern (from Invoice Create / "Download invoice in template"):
 *   `{Client} {Invoice|Proforma|Credit Note|Debit Note} {InvoiceId} ({Mon-YYYY}).pdf`
 * e.g. `Bajaj Invoice SH1697 (Jun-2026).pdf`
 *
 * Source: user spec — Invoice Name = Client Name + Document Type + Invoice ID + (Billing Month MON-YYYY)
 *
 * Prefer `@/utils/invoice-filename` for new imports (also exports the 2 MB size helpers).
 */

import { documentTypeToFilenameLabel } from './invoice-document-types'

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * Strip characters illegal in Windows/macOS filenames.
 */
export function sanitizeInvoiceFilenameBase(name) {
  return String(name || '')
    .replace(/[<>:"/\\|?*\u0000-\u001f]+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Normalize billing month to `Mon-YYYY` (e.g. Jun-2026).
 * Accepts already-formatted values, YYYY-MM, or date-like strings.
 */
export function formatBillingMonthForInvoiceName(raw) {
  if (raw == null || raw === '') return ''
  const s = String(raw).trim()
  if (!s) return ''

  const monYear = s.match(/^([A-Za-z]{3})-(\d{4})$/)
  if (monYear) {
    const mon = monYear[1].charAt(0).toUpperCase() + monYear[1].slice(1).toLowerCase()
    return `${mon}-${monYear[2]}`
  }

  const ymd = s.match(/^(\d{4})-(\d{2})/)
  if (ymd) {
    const idx = parseInt(ymd[2], 10) - 1
    if (idx >= 0 && idx < 12) return `${MONTHS_SHORT[idx]}-${ymd[1]}`
  }

  return s
}

function pickClientName(invoice = {}) {
  const raw = String(
    invoice.client
      || invoice.client_name
      || invoice.clientName
      || (invoice.invoiceClientInfo && invoice.invoiceClientInfo.clientName)
      || '',
  ).trim()
  if (!raw || raw === 'N/A') return ''
  return raw
}

function pickDocLabel(invoice = {}) {
  const docType = String(
    invoice.document_type
      || invoice.documentType
      || invoice.doc_type
      || invoice.docType
      || '',
  )
  return documentTypeToFilenameLabel(docType)
}

function pickInvoiceId(invoice = {}) {
  const id = String(
    invoice.invoiceId
      || invoice.invoice_id
      || invoice.invoice_number
      || invoice.invoiceNumber
      || '',
  ).trim()
  if (!id || id === 'N/A') return ''
  return id
}

/**
 * Build display/save invoice name from parts (does not prefer a stored name).
 * `{Client} {Invoice|Proforma|Credit Note|Debit Note} {InvoiceId} ({Mon-YYYY})`
 */
export function buildAutoInvoiceName(invoice = {}) {
  const client = pickClientName(invoice)
  const docLabel = pickDocLabel(invoice)
  const invoiceId = pickInvoiceId(invoice)
  const billingMonth = formatBillingMonthForInvoiceName(
    invoice.billingMonth || invoice.billing_month,
  )
  const parts = [client, docLabel, invoiceId].filter(Boolean)
  let name = parts.join(' ')
  if (billingMonth) {
    name = name ? `${name} (${billingMonth})` : `(${billingMonth})`
  }
  return name
}

/**
 * Resolve the base name for downloads: prefer stored invoice_name, else rebuild.
 */
export function resolveInvoiceDownloadBaseName(invoice = {}) {
  const stored = String(invoice.invoice_name || invoice.invoiceName || '').trim()
  if (stored) return stored
  return buildAutoInvoiceName(invoice) || 'Invoice'
}

/**
 * Full download filename with extension, sanitized for the filesystem.
 * @param {object} invoice - invoice row / form-like object
 * @param {string} [extension='pdf']
 */
export function buildInvoiceDownloadFilename(invoice = {}, extension = 'pdf') {
  const base = sanitizeInvoiceFilenameBase(resolveInvoiceDownloadBaseName(invoice))
  const ext = String(extension || 'pdf').replace(/^\./, '')
  return `${base || 'Invoice'}.${ext}`
}

/**
 * E-invoice export name: `{invoice_id}-einvoice.json` / `.xlsx`.
 */
export function buildEInvoiceDownloadFilename(invoice = {}, extension = 'json') {
  const raw = pickInvoiceId(invoice) || 'invoice'
  const base = sanitizeInvoiceFilenameBase(raw) || 'invoice'
  const ext = String(extension || 'json').replace(/^\./, '')
  return `${base}-einvoice.${ext}`
}
